// Typed localStorage accessors.
//
// The enrollment flow persists ~40 loosely-named keys across pages. Referencing
// them as bare strings everywhere invites typos (`utility_id` vs `utilityId`)
// and hides the real key set. `get`/`set`/`remove` accept only known keys (a
// typo becomes a compile error); `rawGet`/`rawSet`/`rawRemove` are the escape
// hatch for dynamically-named keys.
//
// Behaviour is a 1:1 wrapper over window.localStorage — `get` returns the raw
// stored string or null, exactly like getItem — so callers migrate mechanically
// with no behaviour change.

// Known keys used across the enrollment flow. Extend as more pages migrate.
export type StorageKey =
  // identity / contact / deal
  | "userEmail"
  | "userId"
  | "contactId"
  | "dealId"
  | "RecordId"
  | "sha1email"
  | "LoggedName"
  | "CompanyName"
  | "FirstName"
  | "LastName"
  | "HomePhone"
  | "CellPhone"
  // utility / farm
  | "utility_id"
  | "utility_Name"
  | "FarmID"
  | "FarmName"
  | "FarmCounty"
  | "FarmStateAbbreviation"
  | "county"
  | "zip_code"
  | "credentialProvider"
  // session / config
  | "Config ID"
  | "SessionDetails"
  | "UtilitySessionDetails"
  | "stateForLMIConfig"
  | "UtilityRate"
  | "SubSizingFactor"
  | "standardRate"
  | "lmiNseRate"
  | "acSystemSize"
  | "acKwh"
  | "usageDisplayelementList"
  | "usagerequireElementList"
  | "consentDisplayelementList"
  | "consentrequireElementList"
  // attribution / tracking
  | "attcode"
  | "repid"
  | "referralUrl"
  | "clickId"
  | "isImpact"
  | "isLMI"
  // device / environment
  | "User Device"
  | "Browser Name"
  | "Operating System"
  // flow control / onboarding
  | "skipUsage"
  | "onboarding_token"
  | "dateStamp"

export function get(key: StorageKey): string | null {
  return localStorage.getItem(key)
}

export function set(key: StorageKey, value: string): void {
  localStorage.setItem(key, value)
}

export function remove(key: StorageKey): void {
  localStorage.removeItem(key)
}

// Clears ALL enrollment state. Kept on the seam so callers never touch
// window.localStorage directly (some flow pages reset everything on entry).
export function clear(): void {
  localStorage.clear()
}

// Escape hatch for dynamically-named keys not in StorageKey.
export function rawGet(key: string): string | null {
  return localStorage.getItem(key)
}

export function rawSet(key: string, value: string): void {
  localStorage.setItem(key, value)
}

export function rawRemove(key: string): void {
  localStorage.removeItem(key)
}
